import React, { BaseSyntheticEvent, useEffect, useRef, useState } from 'react';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import usePopover from 'hooks/usePopover';
import Search from 'housecall-ui/dist/DesignSystem/Inputs/Search/Search';
import Box from 'housecall-ui/dist/DesignSystem/Layout/Box/Box';
import { SEARCH_DEBOUNCE_TIME } from 'resources/PriceBook/Data';

import { getSearchResultsFilter } from '../Utils';

import { useGlobalSearch } from './api/useGlobalSearch';
import SearchResultsContainer from './SearchResultsContainer';

type GlobalSearchContainerProps = {
  responsive?: boolean;
};

const GlobalSearchContainer = ({ responsive }: GlobalSearchContainerProps) => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isTyping, setIsTyping] = useState<boolean>(false);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const { isLoading, isFetching, data } = useGlobalSearch({ searchTerm: debouncedSearchTerm });

  const loading = isLoading || isFetching || isTyping;

  useDebouncedEffect(
    () => {
      setIsTyping(false);
      setDebouncedSearchTerm(searchTerm);
    },
    SEARCH_DEBOUNCE_TIME,
    [searchTerm]
  );

  const searchRef = useRef<HTMLInputElement>(null);

  const {
    open: searchPopoverOpen,
    openPopover: openSearchPopover,
    closePopover: closeSearchPopover,
    anchorEl: searchPopoverAnchor,
  } = usePopover();

  useEffect(() => {
    if (!searchPopoverOpen) {
      if (searchRef?.current) searchRef.current.blur();
      setSearchTerm('');
    }
  }, [searchPopoverOpen]);

  const handleChange = (e: BaseSyntheticEvent) => {
    setSearchTerm(e.target.value);

    if (e.target.value) setIsTyping(true);
  };

  const handleClear = () => setSearchTerm('');

  return (
    <>
      <Box role="presentation" onClick={openSearchPopover} width={responsive ? null : 240}>
        <Search
          inputRef={searchRef}
          value={searchTerm}
          onChange={handleChange}
          onClear={handleClear}
          showClear={searchTerm}
          loading={loading}
          rounded
        />
      </Box>
      <SearchResultsContainer
        open={searchPopoverOpen && searchTerm.length > 0}
        onClose={closeSearchPopover}
        results={getSearchResultsFilter(data)}
        anchorEl={searchPopoverAnchor}
        elementRef={searchRef}
        loading={loading}
        searchTerm={searchTerm}
      />
    </>
  );
};

export default GlobalSearchContainer;
